import { useState, useCallback } from 'react';
import axios from 'axios';
import { 
  API, 
  getAuthHeaders, 
  MAX_FILE_SIZE,
  MAX_FILE_SIZE_MB,
  MAX_FILE_SIZE_WITH_COMPRESSION,
  MAX_FILE_SIZE_WITH_COMPRESSION_MB,
  UPLOAD_TIMEOUT_MS,
  COMPRESSION_TIMEOUT_MS,
  STALE_PROGRESS_TIMEOUT_MS,
  COMPRESSIBLE_EXTENSIONS,
  getFileTypeInfo,
  formatFileSize,
} from '../../lib/libraryUtils';

/**
 * Hook for file upload flow with progress, stale detection, and error handling
 */
export const useUploadFlow = ({ folderId, onUploadComplete }) => {
  const [uploadFiles, setUploadFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  // Add files to upload queue
  const addFiles = useCallback((files, allowedExtensions = []) => {
    const fileArray = Array.from(files);
    const validFiles = [];
    const errors = [];

    fileArray.forEach(file => {
      const extension = file.name.split('.').pop().toLowerCase();
      const isCompressible = COMPRESSIBLE_EXTENSIONS.includes(extension);
      const { resourceType, isImage } = getFileTypeInfo(extension);
      const maxSize = isCompressible ? MAX_FILE_SIZE_WITH_COMPRESSION : MAX_FILE_SIZE;
      
      if (file.size > maxSize) {
        const maxMB = isCompressible ? MAX_FILE_SIZE_WITH_COMPRESSION_MB : MAX_FILE_SIZE_MB;
        errors.push(`${file.name}: Bestand te groot (max ${maxMB}MB)`);
      } else if (file.size === 0) {
        errors.push(`${file.name}: Bestand is leeg`);
      } else if (allowedExtensions.length > 0 && !allowedExtensions.includes(extension)) {
        errors.push(`${file.name}: Type niet toegestaan (.${extension})`);
      } else {
        const needsCompression = file.size > MAX_FILE_SIZE && isCompressible;
        
        validFiles.push({
          file,
          name: file.name,
          size: file.size,
          sizeFormatted: formatFileSize(file.size),
          extension,
          resourceType,
          isImage,
          status: 'pending',
          progress: 0,
          statusText: needsCompression 
            ? `Wordt gecomprimeerd (${formatFileSize(file.size)} → max ${MAX_FILE_SIZE_MB}MB)`
            : '',
          error: null,
          needsCompression
        });
      }
    });

    return { validFiles, errors };
  }, []);

  // Queue files for upload
  const queueFiles = useCallback((files, allowedExtensions = []) => {
    const { validFiles, errors } = addFiles(files, allowedExtensions);
    if (validFiles.length > 0) {
      setUploadFiles(prev => [...prev, ...validFiles]);
    }
    return { validFiles, errors };
  }, [addFiles]);

  // Remove file from queue
  const removeFile = useCallback((index) => {
    setUploadFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Clear all files
  const clearFiles = useCallback(() => {
    setUploadFiles([]);
  }, []);

  // Update file status
  const updateFileStatus = useCallback((index, updates) => {
    setUploadFiles(prev => prev.map((f, i) => 
      i === index ? { ...f, ...updates } : f
    ));
  }, []);

  // Upload with compression (for large files)
  const uploadWithCompression = useCallback(async (fileObj, index, currentFolderId) => {
    const { file } = fileObj;
    const abortController = new AbortController();
    let lastProgressTime = Date.now();
    let staleCheckInterval = null;

    try {
      updateFileStatus(index, { status: 'uploading', progress: 5, statusText: 'Bestand voorbereiden...' });

      const formData = new FormData();
      formData.append('file', file);
      if (currentFolderId) {
        formData.append('folder_id', currentFolderId);
      }

      staleCheckInterval = setInterval(() => {
        if (Date.now() - lastProgressTime > STALE_PROGRESS_TIMEOUT_MS) {
          updateFileStatus(index, { statusText: 'Server verwerkt bestand... (dit kan even duren)' });
        }
      }, 5000);

      await axios.post(
        `${API}/library/uploads/compressed`,
        formData,
        {
          ...getAuthHeaders(),
          headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' },
          timeout: COMPRESSION_TIMEOUT_MS,
          signal: abortController.signal,
          onUploadProgress: (progressEvent) => {
            lastProgressTime = Date.now();
            const uploadPercent = Math.round((progressEvent.loaded / progressEvent.total) * 50);
            let statusText = `Uploading... ${formatFileSize(progressEvent.loaded)} / ${formatFileSize(progressEvent.total)}`;
            if (uploadPercent >= 45) statusText = 'Server ontvangt bestand...';
            if (uploadPercent >= 50) statusText = 'Comprimeren & uploaden naar cloud...';
            updateFileStatus(index, { progress: Math.min(uploadPercent, 50), statusText });
          }
        }
      );

      if (staleCheckInterval) clearInterval(staleCheckInterval);
      updateFileStatus(index, { status: 'success', progress: 100, statusText: '✓ Upload geslaagd' });
      return true;
    } catch (error) {
      if (staleCheckInterval) clearInterval(staleCheckInterval);
      const errorMsg = mapUploadError(error);
      updateFileStatus(index, { status: 'error', error: errorMsg, statusText: '' });
      return false;
    }
  }, [updateFileStatus]);

  // Direct upload to Cloudinary (for small files)
  const uploadDirectToCloudinary = useCallback(async (fileObj, index, currentFolderId) => {
    const { file, extension } = fileObj;
    const abortController = new AbortController();
    let lastProgressTime = Date.now();
    let staleCheckInterval = null;

    try {
      updateFileStatus(index, { status: 'uploading', progress: 5, statusText: 'Handtekening aanvragen...' });

      // Get signature
      const signatureResponse = await axios.post(
        `${API}/library/uploads/signature`,
        { filename: file.name, extension, file_size: file.size, folder_id: currentFolderId },
        { ...getAuthHeaders(), timeout: 30000, signal: abortController.signal }
      );

      const { timestamp, signature, api_key, cloud_name, folder, resource_type } = signatureResponse.data;

      updateFileStatus(index, { progress: 15, statusText: 'Uploading naar cloud...' });

      const formData = new FormData();
      formData.append('file', file);
      formData.append('timestamp', timestamp);
      formData.append('signature', signature);
      formData.append('api_key', api_key);
      formData.append('folder', folder);

      staleCheckInterval = setInterval(() => {
        if (Date.now() - lastProgressTime > STALE_PROGRESS_TIMEOUT_MS) {
          updateFileStatus(index, { statusText: 'Upload bezig... even geduld' });
        }
      }, 5000);

      const cloudinaryResponse = await axios.post(
        `https://api.cloudinary.com/v1_1/${cloud_name}/${resource_type}/upload`,
        formData,
        {
          timeout: UPLOAD_TIMEOUT_MS,
          signal: abortController.signal,
          onUploadProgress: (progressEvent) => {
            lastProgressTime = Date.now();
            const percentCompleted = Math.round(15 + (progressEvent.loaded / progressEvent.total) * 70);
            updateFileStatus(index, { 
              progress: percentCompleted, 
              statusText: `Uploading... ${formatFileSize(progressEvent.loaded)} / ${formatFileSize(progressEvent.total)}` 
            });
          }
        }
      );

      if (staleCheckInterval) clearInterval(staleCheckInterval);
      updateFileStatus(index, { progress: 90, statusText: 'Document registreren...' });

      // Register in database
      await axios.post(`${API}/library/documents`, {
        title: file.name.replace(/\.[^/.]+$/, ''),
        folder_id: currentFolderId,
        cloudinary_public_id: cloudinaryResponse.data.public_id,
        cloudinary_resource_type: resource_type,
        cloudinary_format: cloudinaryResponse.data.format || extension,
        cloudinary_bytes: cloudinaryResponse.data.bytes,
        cloudinary_version: cloudinaryResponse.data.version,
        cloudinary_url: cloudinaryResponse.data.secure_url,
        original_name: file.name,
        extension
      }, { ...getAuthHeaders(), timeout: 30000 });

      updateFileStatus(index, { status: 'success', progress: 100, statusText: '✓ Upload geslaagd' });
      return true;
    } catch (error) {
      if (staleCheckInterval) clearInterval(staleCheckInterval);
      const errorMsg = mapUploadError(error);
      updateFileStatus(index, { status: 'error', error: errorMsg, statusText: '' });
      return false;
    }
  }, [updateFileStatus]);

  // Start uploading all pending files
  const startUpload = useCallback(async (currentFolderId) => {
    const pendingFiles = uploadFiles.filter(f => f.status === 'pending');
    if (pendingFiles.length === 0) return { successCount: 0, failCount: 0 };

    setIsUploading(true);
    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < uploadFiles.length; i++) {
      const fileObj = uploadFiles[i];
      if (fileObj.status !== 'pending') continue;

      const success = fileObj.needsCompression
        ? await uploadWithCompression(fileObj, i, currentFolderId)
        : await uploadDirectToCloudinary(fileObj, i, currentFolderId);

      if (success) successCount++;
      else failCount++;
    }

    setIsUploading(false);
    onUploadComplete?.({ successCount, failCount });
    return { successCount, failCount };
  }, [uploadFiles, uploadWithCompression, uploadDirectToCloudinary, onUploadComplete]);

  // Get counts
  const pendingCount = uploadFiles.filter(f => f.status === 'pending').length;
  const successCount = uploadFiles.filter(f => f.status === 'success').length;
  const errorCount = uploadFiles.filter(f => f.status === 'error').length;

  return {
    uploadFiles,
    isUploading,
    queueFiles,
    removeFile,
    clearFiles,
    startUpload,
    pendingCount,
    successCount,
    errorCount,
  };
};

// Helper: Map upload errors to Dutch messages
function mapUploadError(error) {
  if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
    return 'Upload geannuleerd';
  }
  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    return 'Upload timeout - probeer opnieuw met een kleiner bestand';
  }
  if (error.response?.status === 413) {
    return `Bestand te groot`;
  }
  if (error.response?.status === 401 || error.response?.status === 403) {
    return 'Sessie verlopen - log opnieuw in';
  }
  if (error.response?.data?.error?.message) {
    const msg = error.response.data.error.message;
    if (msg.includes('File size too large')) return 'Bestand te groot';
    if (msg.includes('Invalid signature')) return 'Upload verificatie mislukt';
    return msg;
  }
  if (error.response?.data?.detail) {
    return error.response.data.detail;
  }
  if (!navigator.onLine) {
    return 'Geen internetverbinding';
  }
  return 'Upload mislukt';
}

export default useUploadFlow;
